// Domínio da Trilha do Guru: tipos, carga de conteúdo, progresso e
// gamificação (XP, títulos, sequência de dias). Lógica determinística pura —
// sem React — para ser testável isoladamente. F1: conteúdo empacotado no
// front (JSON por seção, lazy) e progresso em localStorage; quando o core
// passar a persistir progresso, este arquivo vira a camada de serviço.

import {PARAM_CONSULTA, PARAM_PERGUNTA, guardarConsulta} from './ponteConsulta'

export interface Pilula {
    id: string
    texto: string
    pergunta: string
    alternativas: string[]
    correta: number
    explicacao: string
    fonte: string
    // Habilita a ponte "Aprofundar em uma Consulta" no feedback desta pílula.
    // Opcional e ausente nos JSONs publicados: quem popula é
    // `marcarAprofundamento`, na carga (todas as trilhas; `false` explícito
    // no JSON ou na edição do admin desliga pílula a pílula).
    aprofundar?: boolean
}

export interface Secao {
    trilha: string
    ordem: number
    slug: string
    titulo: string
    descricao: string
    pilulas: Pilula[]
}

export interface Trilha {
    id: string
    nome: string
    nivel: string
    descricao: string
}

// Ordem de exibição das trilhas. Só aparecem na tela as que tiverem seções
// carregadas — permite lançar conteúdo por partes.
export const TRILHAS: Trilha[] = [
    {
        id: 'fundamentos',
        nome: 'Jornada do Guru',
        nivel: 'Fundamentos',
        descricao: 'A base da Lei 14.133, do zero ao domínio.',
    },
    {
        id: 'especialista',
        nome: 'Trilha do Especialista',
        nivel: 'Avançado',
        descricao:
            'Aprofundamento doutrinário: nuances, exceções e entendimentos.',
    },
    {
        id: 'polemicas',
        nome: 'Polêmicas & Casos Difíceis',
        nivel: 'Avançado',
        descricao:
            'Controvérsias, jurisprudência do TCU e zonas cinzentas da prática.',
    },
    {
        id: 'jurisprudencia',
        nome: 'Jurisprudência',
        nivel: 'Precedentes',
        descricao:
            'Súmulas e precedentes que todo licitador precisa conhecer — TCU, STF e STJ.',
    },
]

export interface StreakInfo {
    atual: number
    recorde: number
    ultimoDia: string | null
}

export interface Progresso {
    xp: number
    concluidas: Record<string, boolean[]>
    streak: StreakInfo
    // Pílulas respondidas corretamente ao menos uma vez (por id). Refazer
    // lição não aumenta este conjunto. Não é critério de título (o título
    // depende só do XP), mas é a métrica de amplitude exibida ao usuário e
    // agregada no painel admin.
    dominadas: Record<string, true>
    // Estatísticas por pílula (tentativas/erros) — alimentam o painel admin.
    stats: Record<string, {t: number; e: number}>
    // Tamanho (nº de pílulas) de cada seção quando o progresso foi gravado.
    // Se o conteúdo publicado mudar de tamanho, o progresso daquela seção é
    // resetado (as fronteiras das lições se deslocariam).
    tamanhos?: Record<string, number>
}

// O título depende EXCLUSIVAMENTE do XP acumulado. Não há exigência de
// amplitude (pílulas distintas) porque o anti-farm já mora dentro do próprio
// XP: pílula já dominada vale XP_REVISAO_DOMINADA (2) em vez de XP_ACERTO
// (10), e os bônus de lição e de perfeita só saem na primeira conclusão.
// Refazer uma lição rende 10 XP contra 70 de uma lição nova — farmar é ~7x
// menos eficiente que progredir, então a régua de XP já premia amplitude.
export interface Titulo {
    xp: number
    nome: string
}

export interface ResultadoPilula {
    id: string
    primeira: boolean
}

export interface ResultadoLicao {
    xpGanho: number
    xpTotal: number
    dominadasNovas: number
    dominadasTotal: number
    streak: number
    recorde: number
    novoRecorde: boolean
    // Título ANTES e DEPOIS do XP desta lição, e a promoção entre os dois.
    // O gatilho da comemoração de nível é a mudança de TÍTULO, não uma faixa
    // de XP recalculada na tela: assim a régua de `TITULOS` continua sendo o
    // único lugar que decide o que é subir de nível, e mexer nela não deixa
    // uma comemoração fora de sincronia com o rótulo exibido.
    titulo: string
    tituloAnterior: string
    subiuDeTitulo: boolean
}

export const PILULAS_POR_LICAO = 5
export const XP_ACERTO = 10
export const XP_ACERTO_REVISAO = 5
export const XP_REVISAO_DOMINADA = 2
export const XP_BONUS_LICAO = 10
export const XP_BONUS_PERFEITA = 10

// Régua de títulos: rápida no começo (primeiras promoções chegam logo, para
// engajar) e cada vez mais espaçada no fim (Guru é uma jornada longa).
export const TITULOS: Titulo[] = [
    {xp: 0, nome: 'Curioso'},
    {xp: 150, nome: 'Iniciante'},
    {xp: 400, nome: 'Aprendiz'},
    {xp: 1200, nome: 'Estudioso'},
    {xp: 2800, nome: 'Conhecedor'},
    {xp: 5200, nome: 'Especialista'},
    {xp: 8200, nome: 'Mestre'},
    {xp: 12000, nome: 'Guru'},
]

const STORAGE_KEY = 'licito.trilhaGuru.v1'

// Os JSONs de seção (3 trilhas) entram em chunks separados (import
// dinâmico): a trilha não pesa no bundle inicial do app.
const CARREGADORES: Record<string, () => Promise<{default: Secao}>> = {
    sec01: () => import('./data/sec01-fundamentos.json'),
    sec02: () => import('./data/sec02-definicoes.json'),
    sec03: () => import('./data/sec03-agentes.json'),
    sec04: () => import('./data/sec04-modalidades.json'),
    sec05: () => import('./data/sec05-preparatoria.json'),
    sec06: () => import('./data/sec06-precos.json'),
    sec07: () => import('./data/sec07-edital.json'),
    sec08: () => import('./data/sec08-julgamento.json'),
    sec09: () => import('./data/sec09-habilitacao.json'),
    sec10: () => import('./data/sec10-direta.json'),
    sec11: () => import('./data/sec11-contratos.json'),
    sec12: () => import('./data/sec12-execucao.json'),
    sec13: () => import('./data/sec13-sancoes.json'),
    sec14: () => import('./data/sec14-especiais.json'),
    esp01: () => import('./data/esp01-constitucional.json'),
    esp02: () => import('./data/esp02-principios.json'),
    esp03: () => import('./data/esp03-inexigibilidade.json'),
    esp04: () => import('./data/esp04-dispensa.json'),
    esp05: () => import('./data/esp05-regulatoria.json'),
    esp06: () => import('./data/esp06-preparatoria.json'),
    esp07: () => import('./data/esp07-agentes.json'),
    pol01: () => import('./data/pol01-habilitacao.json'),
    pol02: () => import('./data/pol02-julgamento.json'),
    pol03: () => import('./data/pol03-srp.json'),
    pol04: () => import('./data/pol04-contratos.json'),
    pol05: () => import('./data/pol05-execucao.json'),
    pol06: () => import('./data/pol06-equilibrio.json'),
    jur01: () => import('./data/jur01-tcu-licitacao.json'),
    jur02: () => import('./data/jur02-tcu-contratos.json'),
    jur03: () => import('./data/jur03-stf-stj.json'),
}

// Seções como versionadas no repositório, sem alterações do admin.
export async function carregarSecoesBase(): Promise<Secao[]> {
    const modulos = await Promise.all(
        Object.values(CARREGADORES).map((carregar) => carregar())
    )
    return modulos.map((m) => m.default).sort((a, b) => a.ordem - b.ordem)
}

// Seções com as alterações locais do admin aplicadas (edições, desativações
// e acréscimos). É o que a trilha do usuário consome.
export async function carregarSecoes(): Promise<Secao[]> {
    return marcarAprofundamento(
        aplicarOverrides(await carregarSecoesBase(), lerOverrides())
    )
}

/* ------------------------------------------ ponte para o módulo Consultas ---
   O link "Aprofundar em uma Consulta" aparece em TODAS as trilhas (decisão do
   fundador em 2026-08-19, revogando a contenção anterior de 30% que o
   restringia a Polêmicas e Jurisprudência). O rótulo nomeia o destino como o
   usuário o conhece — o módulo se chama Consultas, não "chat". A marcação é
   aplicada na carga, sem reescrever os 30 JSONs de conteúdo; `aprofundar`
   explícito na pílula (JSON ou edição do admin) tem precedência — é a porta
   da curadoria fina, para DESLIGAR caso a caso a pílula em que o link não
   agrega (conteúdo que se esgota na própria explicação). */

export function marcarAprofundamento(secoes: Secao[]): Secao[] {
    return secoes.map((s) => ({
        ...s,
        pilulas: s.pilulas.map((p) =>
            p.aprofundar === undefined ? {...p, aprofundar: true} : p
        ),
    }))
}

/* ------------------------------------------------ prompt da ponte ---------
   Montado DETERMINISTICAMENTE em código a partir da pílula e da resposta
   dada — nenhuma IA escreve o texto do prompt. Ele leva o QUADRO COMPLETO
   (enunciado, as quatro alternativas identificadas, qual é a correta, qual a
   pessoa marcou, a pílula e a base legal): sem isso a resposta do Consultas
   seria uma aula genérica sobre o tema, quando o que a pessoa precisa é da
   correção do raciocínio DELA. Duas variantes, escolhidas aqui e não pelo
   modelo — quem errou precisa ser corrigido, quem acertou precisa avançar.
   O prompt vai PRÉ-PREENCHIDO no chat e não é enviado: o crédito só é
   consumido se a pessoa apertar enter. */

// Alternativa numerada como aparece na tela (chips 1 a 4).
function itemAlternativa(p: Pilula, i: number): string {
    return `${i + 1}. ${p.alternativas[i]}`
}

// `escolhida` é o índice marcado na tela de feedback (a ÚLTIMA resposta, se a
// pílula voltou à fila por erro). `null` só acontece se a ponte for montada
// fora do feedback: cai na variante de aprofundamento, sem acusar erro nenhum.
function acertou(p: Pilula, escolhida: number | null): boolean {
    return escolhida === null || escolhida === p.correta
}

function abertura(p: Pilula, escolhida: number | null): string {
    return acertou(p, escolhida)
        ? 'Estou estudando na Trilha do Guru, respondi corretamente esta questão e quero aprofundar o ponto.'
        : 'Estou estudando na Trilha do Guru e marquei a alternativa errada nesta questão.'
}

function pedido(p: Pilula, escolhida: number | null): string {
    return acertou(p, escolhida)
        ? 'Aprofunde o dispositivo citado: o que ele determina na prática, as controvérsias existentes e o entendimento dos órgãos de controle sobre o tema. Confirme — ou corrija, se estiver incompleto — o entendimento por trás da alternativa correta, e traga um exemplo prático de aplicação em licitações e contratos.'
        : 'Explique, de forma direta: por que a alternativa que eu marquei está errada; onde o raciocínio costuma falhar nesse ponto, ou seja, qual confusão leva a essa escolha; e por que a alternativa correta é a correta, com o que diz o dispositivo. Traga um exemplo prático de aplicação em licitações e contratos.'
}

export function promptAprofundamento(
    p: Pilula,
    escolhida: number | null
): string {
    // Quem acertou marcou a própria alternativa correta: uma linha só, em vez
    // de repetir o mesmo texto duas vezes seguidas.
    const correta = itemAlternativa(p, p.correta)
    const veredito =
        escolhida === null
            ? [`Alternativa correta: ${correta}`]
            : escolhida === p.correta
              ? [
                    `Alternativa correta: ${correta} — foi exatamente a que eu marquei.`,
                ]
              : [
                    `Alternativa correta: ${correta}`,
                    `Alternativa que eu marquei: ${itemAlternativa(
                        p,
                        escolhida
                    )} — está ERRADA.`,
                ]
    return [
        abertura(p, escolhida),
        `Pergunta: ${p.pergunta}`,
        `Alternativas:\n${p.alternativas
            .map((_, i) => itemAlternativa(p, i))
            .join('\n')}`,
        ...veredito,
        `O que a pílula afirma: ${p.texto}`,
        `Base legal citada: ${p.fonte}`,
        pedido(p, escolhida),
    ].join('\n\n')
}

/* Versão enxuta, para quando o prompt precisa caber numa query string (ver
   `urlAprofundamento`). Sai a parte volumosa — as quatro alternativas — e os
   campos longos são cortados. A conta que manda aqui é a do ENCODE: em
   português, com acentos e quebras de linha, o percent-encoding infla o texto
   em ~55% (medido no conteúdo publicado). Só tirar as alternativas não
   bastava: a pílula mais longa com link (jur02-p009) ainda dava 2.014
   caracteres de URL, colado no limite prático de ~2.000. Com os campos
   cortados em 300 caracteres a mesma pílula cai para ~1.400.
   É um degradê consciente: perde-se a correção alternativa a alternativa e o
   fim do texto da pílula, mas a pergunta, o dispositivo e o veredito (errou
   ou acertou) chegam — e isso só acontece com o storage bloqueado. */

const LIMITE_CAMPO_REDUZIDO = 300
// Teto do texto JÁ ENCODADO, com margem para o resto da URL (origem + rota).
const LIMITE_QUERY_ENCODADA = 1800

// Corte determinístico na última palavra inteira que couber.
function resumir(t: string, limite: number): string {
    if (t.length <= limite) return t
    const corte = t.slice(0, limite)
    const espaco = corte.lastIndexOf(' ')
    return `${corte.slice(0, espaco > limite / 2 ? espaco : limite).trimEnd()}…`
}

export function promptAprofundamentoReduzido(
    p: Pilula,
    escolhida: number | null
): string {
    const montar = (comPilula: boolean, limite: number) =>
        [
            abertura(p, escolhida),
            `Pergunta: ${resumir(p.pergunta, limite)}`,
            ...(comPilula
                ? [`O que a pílula afirma: ${resumir(p.texto, limite)}`]
                : []),
            `Base legal citada: ${resumir(p.fonte, limite)}`,
            acertou(p, escolhida)
                ? 'Aprofunde o dispositivo citado: o que ele determina na prática, as controvérsias existentes, o entendimento dos órgãos de controle e um exemplo prático de aplicação em licitações e contratos.'
                : 'Explique onde o raciocínio costuma falhar nesse ponto e qual é a resposta correta, com o que diz o dispositivo e um exemplo prático de aplicação em licitações e contratos.',
        ].join('\n\n')

    const cabe = (t: string) =>
        encodeURIComponent(t).length <= LIMITE_QUERY_ENCODADA

    const completo = montar(true, LIMITE_CAMPO_REDUZIDO)
    if (cabe(completo)) return completo

    // Garantia DURA de tamanho, agora de verdade: o texto da pílula sai
    // inteiro e, se ainda não couber (pergunta e fonte no teto, carregadas de
    // acentos que encodados custam 6 caracteres cada), os campos encolhem
    // pela metade até caber. Termina sempre: a moldura fixa (abertura +
    // pedido) cabe sozinha com folga larga dentro do limite.
    let limite = LIMITE_CAMPO_REDUZIDO
    let candidato = montar(false, limite)
    while (!cabe(candidato) && limite > 1) {
        limite = Math.floor(limite / 2)
        candidato = montar(false, limite)
    }
    return candidato
}

// URL da aba nova do módulo Consultas (rota raiz). O prompt NÃO vai na URL:
// com as quatro alternativas ele passa de 3.500 caracteres encodados, muito
// além do que é seguro numa query string. Vai para o localStorage sob uma
// chave de uso único e a URL leva só a referência (ver ./ponteConsulta).
// Storage indisponível (modo restrito, quota): volta ao transporte antigo,
// com o prompt reduzido, que cabe com folga.
export function urlAprofundamento(p: Pilula, escolhida: number | null): string {
    const referencia = guardarConsulta(promptAprofundamento(p, escolhida))
    if (referencia) return `/?${PARAM_CONSULTA}=${referencia}`
    return `/?${PARAM_PERGUNTA}=${encodeURIComponent(
        promptAprofundamentoReduzido(p, escolhida)
    )}`
}

/* ---------------------- administração de conteúdo (F1: overrides locais) ---
   Em F1 o conteúdo é empacotado no bundle; as alterações do admin ficam em
   localStorage deste navegador e são aplicadas na carga. O caminho oficial
   para publicar uma alteração é exportar o JSON da seção (botão no painel) e
   versioná-lo no repositório. Em F2, conteúdo e edição migram para o backend. */

const OVERRIDES_KEY = 'licito.trilhaGuru.admin.overrides.v1'

export interface Overrides {
    editadas: Record<string, Pilula>
    desativadas: string[]
    adicionadas: Record<string, Pilula[]>
}

export function overridesIniciais(): Overrides {
    return {editadas: {}, desativadas: [], adicionadas: {}}
}

export function lerOverrides(): Overrides {
    try {
        const bruto = localStorage.getItem(OVERRIDES_KEY)
        if (!bruto) return overridesIniciais()
        const o = JSON.parse(bruto)
        const base = overridesIniciais()
        return {
            editadas:
                o?.editadas && typeof o.editadas === 'object'
                    ? o.editadas
                    : base.editadas,
            desativadas: Array.isArray(o?.desativadas)
                ? o.desativadas
                : base.desativadas,
            adicionadas:
                o?.adicionadas && typeof o.adicionadas === 'object'
                    ? o.adicionadas
                    : base.adicionadas,
        }
    } catch {
        return overridesIniciais()
    }
}

export function salvarOverrides(o: Overrides) {
    try {
        localStorage.setItem(OVERRIDES_KEY, JSON.stringify(o))
    } catch {
        /* noop */
    }
}

/* Seção com override é MODO PREVIEW: o conteúdo jogado neste navegador não é
   o publicado. Sessão de preview não pode sair do navegador — o tamanho
   mutado da seção dispararia a reconciliação no servidor e RESETARIA a seção
   na conta do próprio admin; e respostas a pílulas que só existem aqui
   contaminariam as estatísticas da organização com ids fantasmas. O id de
   pílula carrega o slug da seção como prefixo (`sec01-p3`), e o hífen do
   prefixo impede que um slug case com outro que o contenha. */

export function secaoComOverride(
    slug: string,
    o: Overrides = lerOverrides()
): boolean {
    const daSecao = (id: string) => id.startsWith(`${slug}-`)
    return (
        (o.adicionadas[slug] ?? []).length > 0 ||
        o.desativadas.some(daSecao) ||
        Object.keys(o.editadas).some(daSecao)
    )
}

// Pílula que só existe neste navegador (acrescentada pelo admin e ainda não
// publicada): a resposta dela não pode viajar para o servidor.
export function pilulaAdicionadaLocalmente(
    id: string,
    o: Overrides = lerOverrides()
): boolean {
    return Object.values(o.adicionadas).some((lista) =>
        lista.some((p) => p.id === id)
    )
}

export function limparOverridesDaSecao(slug: string, ids: string[]) {
    const o = lerOverrides()
    for (const id of ids) delete o.editadas[id]
    o.desativadas = o.desativadas.filter((id) => !ids.includes(id))
    delete o.adicionadas[slug]
    salvarOverrides(o)
}

export function aplicarOverrides(secoes: Secao[], o: Overrides): Secao[] {
    return secoes.map((s) => ({
        ...s,
        pilulas: s.pilulas
            .filter((p) => !o.desativadas.includes(p.id))
            .map((p) => o.editadas[p.id] ?? p)
            // pílula "adicionada" que já foi publicada na base (fluxo de
            // exportar → versionar) não pode entrar duplicada
            .concat(
                (o.adicionadas[s.slug] ?? []).filter(
                    (n) => !s.pilulas.some((b) => b.id === n.id)
                )
            ),
    }))
}

export function secoesDaTrilha(secoes: Secao[], trilhaId: string): Secao[] {
    return secoes.filter((s) => s.trilha === trilhaId)
}

export function trilhasDisponiveis(secoes: Secao[]): Trilha[] {
    return TRILHAS.filter((t) => secoes.some((s) => s.trilha === t.id))
}

export function licoesDe(secao: Secao): Pilula[][] {
    const licoes: Pilula[][] = []
    for (let i = 0; i < secao.pilulas.length; i += PILULAS_POR_LICAO) {
        licoes.push(secao.pilulas.slice(i, i + PILULAS_POR_LICAO))
    }
    return licoes
}

export function progressoInicial(): Progresso {
    return {
        xp: 0,
        concluidas: {},
        streak: {atual: 0, recorde: 0, ultimoDia: null},
        dominadas: {},
        stats: {},
    }
}

/* Migração do campo `xpRevisao` (removido quando a revisão ganhou endpoint).
   Ele guardava a parcela de `xp` ganha revisando erros, porque o servidor não
   conhecia a revisão e o doc dele sobrescreve o cache — a parcela era somada
   de volta a cada sincronização. Com POST /revisoes/concluir o XP da revisão
   nasce no servidor como o de qualquer lição, e a reaplicação sairia contando
   em dobro.

   O caminho escolhido é o mais simples que não mexe no que o usuário vê: o
   campo é IGNORADO na leitura e some na próxima gravação, mas `xp` fica
   intacto — ele sempre foi o total (mapa + revisão), então na primeira carga
   após a atualização nada salta nem some. Para quem ainda não sincronizou,
   `localParaServidor` agora importa esse total inteiro, e a parcela histórica
   vai junto para o servidor.

   O caso que perde: quem JÁ tinha progresso no servidor vê, na primeira
   sincronização, o total convergir para o do servidor — sem a parcela ganha
   revisando durante a janela em que ela era local-only (poucos XP: uma sessão
   de revisão rende 2 por pílula já dominada). É deliberado. As alternativas
   eram manter para sempre um override local do XP do servidor, que é
   exatamente a gambiarra sendo removida, ou sintetizar uma sessão de revisão
   para "cobrar" a diferença — o que sujaria as estatísticas por pílula do
   admin com respostas que nunca aconteceram. */
export function lerProgresso(): Progresso {
    try {
        const bruto = localStorage.getItem(STORAGE_KEY)
        if (!bruto) return progressoInicial()
        const p = JSON.parse(bruto)
        if (typeof p?.xp !== 'number') return progressoInicial()
        // Normaliza shapes parciais (rollback/edição manual da chave): cada
        // campo ausente ou de tipo errado volta ao inicial sem quebrar render.
        const base = progressoInicial()
        const objeto = (v: unknown) =>
            v && typeof v === 'object' ? (v as Record<string, never>) : null
        return {
            xp: p.xp,
            concluidas: objeto(p.concluidas) ?? base.concluidas,
            streak: objeto(p.streak)
                ? {...base.streak, ...p.streak}
                : base.streak,
            dominadas: objeto(p.dominadas) ?? base.dominadas,
            stats: objeto(p.stats) ?? base.stats,
            // `p.xpRevisao` (shape antigo) é deliberadamente descartado: `xp`
            // já o inclui. Ver a nota de migração acima.
            tamanhos: objeto(p.tamanhos) ?? undefined,
        }
    } catch {
        return progressoInicial()
    }
}

function salvarProgresso(p: Progresso) {
    // Storage bloqueado (SecurityError/quota): progresso vira efêmero, mas a
    // lição conclui normalmente.
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(p))
    } catch {
        /* noop */
    }
}

// Chamar após carregar o conteúdo: se o nº de pílulas de uma seção publicada
// mudou desde a última gravação, o progresso daquela seção (e só dela) é
// resetado — as fronteiras das lições teriam se deslocado.
export function reconciliarProgresso(secoes: Secao[]) {
    const p = lerProgresso()
    const tamanhos: Record<string, number> = {}
    let mudou = false
    for (const s of secoes) {
        tamanhos[s.slug] = s.pilulas.length
        const anterior = p.tamanhos?.[s.slug]
        if (anterior !== undefined && anterior !== s.pilulas.length) {
            delete p.concluidas[s.slug]
            mudou = true
        }
    }
    if (mudou || JSON.stringify(p.tamanhos) !== JSON.stringify(tamanhos)) {
        p.tamanhos = tamanhos
        salvarProgresso(p)
    }
}

export function zerarProgresso() {
    localStorage.removeItem(STORAGE_KEY)
}

// Sobrescreve o cache local com o progresso vindo do servidor (F2 — o
// academy-service é a fonte da verdade; localStorage vira cache).
export function sobrescreverProgresso(p: Progresso) {
    salvarProgresso(p)
}

export function licaoConcluida(
    p: Progresso,
    slug: string,
    licao: number
): boolean {
    return Boolean(p.concluidas[slug]?.[licao])
}

export type EstadoLicao = 'concluida' | 'atual' | 'bloqueada'

// Desbloqueio sequencial DENTRO de uma trilha (trilhas são independentes
// entre si): a primeira lição não concluída é a "atual"; as seguintes ficam
// bloqueadas. Passe apenas as seções da trilha selecionada.
export function estadosDasLicoes(
    secoes: Secao[],
    p: Progresso
): Record<string, EstadoLicao> {
    const estados: Record<string, EstadoLicao> = {}
    let atualEncontrada = false
    for (const secao of secoes) {
        licoesDe(secao).forEach((_, licao) => {
            const chave = `${secao.slug}:${licao}`
            if (licaoConcluida(p, secao.slug, licao)) {
                estados[chave] = 'concluida'
            } else if (!atualEncontrada) {
                estados[chave] = 'atual'
                atualEncontrada = true
            } else {
                estados[chave] = 'bloqueada'
            }
        })
    }
    return estados
}

// A lição "atual" (a do balão pulsante no mapa), com o contexto necessário
// para o card de retomada: em qual trilha e seção ela está e qual a sua
// posição dentro da seção.
export interface LicaoAtual {
    trilha: string
    secao: Secao
    licao: number
    totalNaSecao: number
}

export function licaoAtualDaTrilha(
    secoes: Secao[],
    p: Progresso,
    trilhaId: string
): LicaoAtual | null {
    const daTrilha = secoesDaTrilha(secoes, trilhaId)
    const estados = estadosDasLicoes(daTrilha, p)
    for (const secao of daTrilha) {
        const licoes = licoesDe(secao)
        for (let licao = 0; licao < licoes.length; licao += 1) {
            if (estados[`${secao.slug}:${licao}`] === 'atual') {
                return {
                    trilha: trilhaId,
                    secao,
                    licao,
                    totalNaSecao: licoes.length,
                }
            }
        }
    }
    return null
}

// Onde a pessoa parou. A trilha aberta na tela tem preferência (é o balão que
// ela veria rolando a página); se ela já concluiu essa trilha inteira, aponta
// para a próxima trilha com lição pendente — quem chama troca a aba ativa.
// Null = concluiu tudo o que está publicado.
export function licaoAtual(
    secoes: Secao[],
    p: Progresso,
    trilhaPreferida: string
): LicaoAtual | null {
    const preferida = licaoAtualDaTrilha(secoes, p, trilhaPreferida)
    if (preferida) return preferida
    for (const t of trilhasDisponiveis(secoes)) {
        if (t.id === trilhaPreferida) continue
        const outra = licaoAtualDaTrilha(secoes, p, t.id)
        if (outra) return outra
    }
    return null
}

// Uma trilha 100% concluída: toda lição de toda seção PUBLICADA da trilha
// marcada como concluída. Passe apenas as seções da trilha (ver
// `secoesDaTrilha`). Trilha sem seção publicada não está concluída — não se
// conclui o que ainda não existe, e é isso que impede o certificado de
// aparecer numa trilha vazia.
export function trilhaConcluida(secoes: Secao[], p: Progresso): boolean {
    let alguma = false
    for (const secao of secoes) {
        const licoes = licoesDe(secao)
        alguma = alguma || licoes.length > 0
        for (let licao = 0; licao < licoes.length; licao += 1) {
            if (!licaoConcluida(p, secao.slug, licao)) return false
        }
    }
    return alguma
}

// Pílulas dominadas DENTRO desta trilha — o número que vai no certificado.
// `dominadas` é global (id → true), então a contagem tem de ser feita contra
// as pílulas publicadas da trilha; conteúdo desativado pelo admin sai da conta
// junto com o mapa.
export function dominadasDaTrilha(secoes: Secao[], p: Progresso): number {
    return secoes.reduce(
        (total, secao) =>
            total + secao.pilulas.filter((x) => p.dominadas[x.id]).length,
        0
    )
}

// Dia "oficial" do produto: fuso de Brasília (America/Sao_Paulo) — as mesmas
// viradas de dia que o academy-service usa no streak; en-CA = YYYY-MM-DD.
// O deslocamento é aplicado em MILISSEGUNDOS, não com `setDate`: setDate anda
// um dia do calendário LOCAL da máquina, que numa virada de horário de verão
// (EUA, Europa) tem 23 ou 25 horas — perto da meia-noite de Brasília isso
// devolveria o dia errado. São Paulo não tem mais horário de verão, então
// 24h exatas sempre caem no dia anterior/seguinte de Brasília.
const UM_DIA_EM_MS = 24 * 60 * 60 * 1000

// Formatter içado para o módulo: construir Intl.DateTimeFormat é caro
// (carrega dados de locale/fuso) e a configuração é fixa — dataLocal roda a
// cada render de streak, só o format() precisa ser por chamada.
const FORMATADOR_DIA_SP = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
})

function dataLocal(deslocamentoDias = 0): string {
    return FORMATADOR_DIA_SP.format(
        new Date(Date.now() + deslocamentoDias * UM_DIA_EM_MS)
    )
}

// Sequência exibida: se o último estudo foi antes de ontem, já mostra 0
// (a sequência quebrou), mesmo antes de concluir nova lição.
export function streakVigente(p: Progresso): number {
    const {ultimoDia, atual} = p.streak
    if (!ultimoDia) return 0
    if (ultimoDia === dataLocal() || ultimoDia === dataLocal(-1)) return atual
    return 0
}

// Sequência prestes a morrer: a pessoa estudou ONTEM e ainda não estudou
// hoje — hoje é o último dia para não perdê-la. Devolve os dias EM JOGO, e 0
// quando não há nada a perder: já estudou hoje (ultimoDia é hoje), a sequência
// já quebrou (ultimoDia mais velho que ontem, e `streakVigente` já mostra 0)
// ou nunca começou. Quem chama pode tratar o retorno como booleano, mas o
// número é o que a mensagem precisa exibir.
export function sequenciaEmRisco(p: Progresso): number {
    if (p.streak.ultimoDia !== dataLocal(-1)) return 0
    return streakVigente(p)
}

function atualizarStreak(streak: StreakInfo): {
    streak: number
    novoRecorde: boolean
} {
    if (streak.ultimoDia !== dataLocal()) {
        streak.atual = streak.ultimoDia === dataLocal(-1) ? streak.atual + 1 : 1
        streak.ultimoDia = dataLocal()
    }
    const novoRecorde = streak.atual > streak.recorde
    if (novoRecorde) streak.recorde = streak.atual
    return {streak: streak.atual, novoRecorde}
}

// XP da lição: pílula inédita vale cheio (10 de primeira / 5 na revisão do
// erro); pílula já dominada em lição refeita vale quase nada (2). Bônus de
// lição e de perfeita só na primeira conclusão — refazer não é caminho para
// Guru, dominar conteúdo novo é.
export function calcularXpDaLicao(
    resultados: ResultadoPilula[],
    dominadas: Record<string, true>,
    primeiraConclusao: boolean
): {xp: number; novas: number} {
    let xp = 0
    let novas = 0
    for (const r of resultados) {
        if (dominadas[r.id]) {
            xp += XP_REVISAO_DOMINADA
        } else {
            novas += 1
            xp += r.primeira ? XP_ACERTO : XP_ACERTO_REVISAO
        }
    }
    if (primeiraConclusao) {
        xp += XP_BONUS_LICAO
        if (resultados.every((r) => r.primeira)) xp += XP_BONUS_PERFEITA
    }
    return {xp, novas}
}

export function contarDominadas(p: Progresso): number {
    return Object.keys(p.dominadas).length
}

// Onde marcar a conclusão no mapa. `null` = lição de revisão dos erros: ela
// soma XP e stats pela regra normal, mas NÃO é lição do mapa — marcar
// `concluidas` ali corromperia o desbloqueio sequencial das seções.
type DestinoNoMapa = {slug: string; licao: number} | null

function aplicarConclusao(
    resultados: ResultadoPilula[],
    destino: DestinoNoMapa
): ResultadoLicao {
    const p = lerProgresso()
    // Sem destino no mapa não existe "primeira conclusão": a revisão nunca
    // paga bônus de lição/perfeita — só o XP por pílula (2 para as já
    // dominadas, que é o caso normal de quem está revisando erro).
    const primeiraConclusao = destino
        ? !licaoConcluida(p, destino.slug, destino.licao)
        : false
    const {xp: xpGanho, novas} = calcularXpDaLicao(
        resultados,
        p.dominadas,
        primeiraConclusao
    )
    // Lido antes da soma: depois de `p.xp += xpGanho` o título de partida
    // não existe mais em lugar nenhum.
    const tituloAnterior = tituloAtual(p.xp).nome

    // Aplicação local otimista (UX imediata), igual para lição e revisão: o
    // doc do servidor sobrescreve o total logo depois, sem somar em cima.
    p.xp += xpGanho
    for (const r of resultados) {
        p.dominadas[r.id] = true
        const s = p.stats[r.id] ?? {t: 0, e: 0}
        s.t += 1
        if (!r.primeira) s.e += 1
        // Acertar de primeira na REVISÃO redime a pílula: zera o contador de
        // erro e ela sai de "o que você já errou" até errar de novo. Redimir
        // é papel só da revisão — a lição do mapa segue somando histórico.
        else if (!destino) s.e = 0
        p.stats[r.id] = s
    }
    if (destino) {
        if (!p.concluidas[destino.slug]) p.concluidas[destino.slug] = []
        p.concluidas[destino.slug][destino.licao] = true
    }
    const {streak, novoRecorde} = atualizarStreak(p.streak)
    salvarProgresso(p)

    const titulo = tituloAtual(p.xp).nome

    return {
        xpGanho,
        xpTotal: p.xp,
        dominadasNovas: novas,
        dominadasTotal: contarDominadas(p),
        streak,
        recorde: p.streak.recorde,
        novoRecorde,
        titulo,
        tituloAnterior,
        subiuDeTitulo: titulo !== tituloAnterior,
    }
}

export function concluirLicao(
    slug: string,
    licao: number,
    resultados: ResultadoPilula[]
): ResultadoLicao {
    return aplicarConclusao(resultados, {slug, licao})
}

/* --------------------------------------------- lição de revisão dos erros ---
   Uma lição montada só com as pílulas cujo contador de erro está de pé
   (stats[id].e > 0), atravessando seções e trilhas. Não pertence ao mapa: não
   marca seção/lição como concluída (ver `aplicarConclusao`), mas conta XP e
   registra as tentativas em stats como qualquer outra resposta. Acertar de
   primeira aqui ZERA o contador da pílula — revisar e acertar esvazia o card
   de revisão; errar de novo recomeça a dívida. */

// Teto por sessão: revisar 40 pílulas de uma vez vira maratona e ninguém
// termina. As piores primeiro; o resto fica para a próxima rodada.
export const MAX_PILULAS_REVISAO = 10

// Pílulas erradas ao menos uma vez, das que mais erram para as que menos
// erram (empate: ordem estável por id, para a lista não dançar entre
// renders). Só entram pílulas ainda publicadas — conteúdo desativado pelo
// admin some da revisão junto com o mapa.
export function pilulasComErro(secoes: Secao[], p: Progresso): Pilula[] {
    return secoes
        .flatMap((s) => s.pilulas)
        .filter((pilula) => (p.stats[pilula.id]?.e ?? 0) > 0)
        .sort(
            (a, b) =>
                (p.stats[b.id]?.e ?? 0) - (p.stats[a.id]?.e ?? 0) ||
                a.id.localeCompare(b.id)
        )
}

export function licaoDeRevisao(
    secoes: Secao[],
    p: Progresso,
    limite = MAX_PILULAS_REVISAO
): Pilula[] {
    return pilulasComErro(secoes, p).slice(0, limite)
}

export function concluirRevisao(resultados: ResultadoPilula[]): ResultadoLicao {
    return aplicarConclusao(resultados, null)
}

// Título é função só do XP acumulado (ver comentário de `Titulo`).
export function tituloAtual(xp: number): Titulo {
    return [...TITULOS].reverse().find((t) => xp >= t.xp) ?? TITULOS[0]
}

export function proximoTitulo(xp: number): Titulo | null {
    const atual = tituloAtual(xp)
    const i = TITULOS.indexOf(atual)
    return TITULOS[i + 1] ?? null
}
